import React, { FC, MouseEvent, useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useQuery } from 'react-query';

import dayjs from 'dayjs';

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	ListItemIcon,
	ListItemText,
	makeStyles,
	Menu,
	MenuItem,
	Modal,
	Paper,
	PaperProps,
	Select,
	Typography
} from '@material-ui/core';

import { Alert } from '@material-ui/lab';

import MaterialTable from 'material-table';
import MenuIcon from '@material-ui/icons/Menu';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CallMissedOutgoingIcon from '@material-ui/icons/CallMissedOutgoing';
import CloseIcon from '@material-ui/icons/Close';

import { useDrmMediaUrls } from '../../hooks';
import VideoPlayer from '../common/VideoPlayer';
import { updateAiringMutation } from '../airings/apiMutations';
import { Airing, DRMTYPE, HLS_TRANSPORT, PbP, Status } from '../../utils/types';
import { convertAiringToAiringInput } from '../../utils/inputTransform';
import { fetchFriendlyStatus } from '../../utils/typeTranslate';
import { getRequest } from '../../utils/httpClient';

const CONFIDENCE_DISPLAY_THRESHOLD = 0.9;
const EXCITEMENT_DISPLAY_THRESHOLD = 70;

const AiringActionMenu: FC<AiringActionMenuProps> = (props) => {
	const { airing, gameId, reloadAirings, sport } = props;
	const { id: airingId, status, channel } = airing;

	let hlsVideoUrl: string;
	if (channel?.sourceAssets?.items) {
		const hlsUrl =
			channel.sourceAssets.items.find((source) => source.id === 'HLS') || channel.sourceAssets.items.find((source) => source.format === 'HLS');
		if (hlsUrl) hlsVideoUrl = hlsUrl.url;
	}

	const drmHlsSource = airing.sources?.find(({ transport }): boolean => transport === HLS_TRANSPORT);
	
	const mediaDataUrls  = useDrmMediaUrls({drmHlsSource, status: airing.status})

	const [updateAiring] = useMutation(updateAiringMutation);

	const { data: interestingPbpData, refetch: refetchInterestingPbpData } = useQuery(
		['alignment', airingId],
		() => getRequest('sports-data', `/getLatestAlignment?airingId=${airingId}&sport=${sport}`),
		{
			enabled: false
		}
	);

	const getParsedInterestingPbpData = (): PbP[] =>
		(interestingPbpData && interestingPbpData.data ? JSON.parse(interestingPbpData.data).playByPlay : []).filter((pbp: PbP) => {
			const confidencePass = pbp.scrubberInfo?.confidence !== undefined && pbp.scrubberInfo.confidence < CONFIDENCE_DISPLAY_THRESHOLD;

			const excitementPass = pbp.has_excitement && pbp.excitement_score !== undefined && pbp.excitement_score < EXCITEMENT_DISPLAY_THRESHOLD;

			return confidencePass || excitementPass;
		});

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const [dialogOpen, setDialogOpen] = useState('');
	const [airingChannel, setAiringChannel] = useState('');
	const [airingStatus, setAiringStatus] = useState('');

	const [pbpConfModalOpen, setPbpConfModalOpen] = useState(false);
	const [airingBypasses, setAiringBypasses] = useState<string[]>([]);
	const [selectedPbp, setSelectedPbp] = useState<string>('');
	const [bypassAltered, setBypassAltered] = useState<boolean>(false);

	useEffect(() => {
		if (pbpConfModalOpen) refetchInterestingPbpData();
	}, [pbpConfModalOpen, refetchInterestingPbpData]);

	const getPbpStartEnd = (time: number): string[] => {
		const startTime = dayjs(time).subtract(20, 'second');
		return [startTime.toISOString(), dayjs(startTime).add(40, 'second').toISOString()];
	};

	const getBypassedPbpData = (): PbP[] => {
		if (!airingBypasses || airingBypasses.length === 0) return [];

		const pInterestingPbpData = getParsedInterestingPbpData();
		if (!pInterestingPbpData) return [];

		return pInterestingPbpData.filter((pbp) => airingBypasses.includes(pbp.id));
	};
	const getNonBypassedPbpData = (): PbP[] => {
		const pInterestingPbpData = getParsedInterestingPbpData();
		if (!airingBypasses || airingBypasses.length === 0) return pInterestingPbpData;

		return pInterestingPbpData.filter((pbp) => !airingBypasses.includes(pbp.id));
	};

	const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
		setAnchorEl(event.currentTarget);
	};

	const percentageFormatter = (num: number): string => `${Math.round(num * 100)}%`;

	const open = Boolean(anchorEl);

	const startChallenge = (airingId: string, airingChannel: string, airingStatus: string): void => {
		setDialogOpen(airingId);
		setAiringChannel(airingChannel);
		setAiringStatus(airingStatus);
	};

	const updateAiringStatus = async (): Promise<void> => {
		const airingInput = convertAiringToAiringInput(airing);
		airingInput.status = airingStatus as Status;

		await updateAiring({
			variables: { airing: airingInput, gameId }
		});

		reloadAirings();
		cancelChallenge();
	};

	const cancelChallenge = (): void => {
		setAnchorEl(null);
		setDialogOpen('');
		setAiringChannel('');
		setAiringStatus('');
	};

	const startPbpConf = (airing: Airing): void => {
		setAiringBypasses(airing.exclusionPlayIds || []);
		setPbpConfModalOpen(true);
		setAnchorEl(null);
	};

	const cancelPbpConf = (): void => {
		setPbpConfModalOpen(false);
		setAiringBypasses([]);
		setBypassAltered(false);
	};

	const addPbpBypass = async (): Promise<unknown> => {
		setAiringBypasses([...airingBypasses, selectedPbp]);
		setSelectedPbp('');
		setBypassAltered(true);
		return;
	};

	const deletePbpBypass = async (pbp: PbP): Promise<unknown> => {
		setAiringBypasses(airingBypasses.filter((id) => id !== pbp.id));
		setBypassAltered(true);
		return;
	};

	const savePbpBypass = async (): Promise<unknown> => {
		const airingInput = convertAiringToAiringInput(airing);
		airingInput.exclusionPlayIds = airingBypasses;
		await updateAiring({
			variables: { airing: airingInput, gameId }
		});
		cancelPbpConf();
		reloadAirings();
		refetchInterestingPbpData();
		return;
	};

	const useStyles = makeStyles((theme) => ({
		centralModal: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		},
		modalBox: {
			background: '#fff',
			minHeight: 700,
			maxHeight: 700,
			minWidth: 900,
			maxWidth: 900,
			overflowY: 'hidden',
			margin: '10vh',
			borderRadius: 4
		},
		modalToolbar: {
			backgroundColor: '#1976d2',
			color: 'white',
			paddingLeft: '1vw',
			paddingTop: '1vh',
			paddingBottom: '1vh',
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center'
		},
		modalContent: {
			minHeight: 590,
			maxHeight: 590,
			padding: '1vw',
			paddingBottom: '0',
			display: 'flex',
			flexDirection: 'column'
		},
		modalFooter: {
			paddingTop: '0.2vh',
			paddingRight: '1vw',
			textAlign: 'right'
		},
		titleBox: {
			display: 'flex',
			alignItems: 'center'
		},
		player: {
			display: 'block',
			float: 'left',
			marginRight: '2rem'
		},
		dl: {
			display: 'inline',
			padding: 0,
			margin: 0
		},
		dt: {
			display: 'inline-block',
			width: 110,
			fontWeight: 'bold'
		},
		dd: {
			display: 'inline-block',
			width: 290
		}
	}));
	const classes = useStyles();

	const dialogIconStyle = { marginRight: '1rem', verticalAlign: 'middle' };

	const pbpColumns = [
		{
			title: 'Play-by-Play',
			field: 'id',
			render: (pbp: PbP): JSX.Element => {
				const [start, end] = getPbpStartEnd(pbp?.scrubberInfo?.videoTime ?? pbp?.exact_video_time!);

				return (
					<>
						{airing.drm && drmHlsSource && mediaDataUrls.licenseUrl && mediaDataUrls.videoUrl && (
							<div className={classes.player}>
								<VideoPlayer
									autoplay={false}
									height='60px'
									drmType={drmHlsSource.protection.toLowerCase() as DRMTYPE}
									laSource={mediaDataUrls.licenseUrl}
									source={mediaDataUrls.videoUrl}
									timeShift={true}
									videoEnd={end}
									videoStart={start}
								/>
							</div>
						)}
						{(!airing.drm || !drmHlsSource) && hlsVideoUrl && (
							<div className={classes.player}>
								<VideoPlayer autoplay={false} height='60px' source={hlsVideoUrl} timeShift={false} videoEnd={end} videoStart={start} />
							</div>
						)}
						<Grid container component='dl' spacing={1} className={classes.dl}>
							<Grid inputMode=''>
								<Typography component='dt' variant='body1' className={classes.dt}>
									Time:
								</Typography>
								<Typography component='dd' variant='body2' className={classes.dd}>
									{pbp.minute}' {pbp.second}"
								</Typography>
							</Grid>
							<Grid item>
								<Typography component='dt' variant='body1' className={classes.dt}>
									Type:
								</Typography>
								<Typography component='dd' variant='body2' className={classes.dd}>
									{pbp.playType.toUpperCase()}
								</Typography>
							</Grid>
							<Grid item>
								<Typography component='dt' variant='body1' className={classes.dt}>
									{pbp.excitement_score ? 'Excitement' : 'Confidence'}:
								</Typography>
								<Typography component='dd' variant='body2' className={classes.dd} style={{ color: 'red' }}>
									{pbp.excitement_score && pbp.excitement_score !== undefined
										? `${pbp.excitement_score}%`
										: percentageFormatter(pbp?.scrubberInfo?.confidence ?? 0)}
								</Typography>
							</Grid>
							<Grid item>
								<Typography component='dt' variant='body1' className={classes.dt}>
									Player:
								</Typography>
								<Typography component='dd' variant='body2' className={classes.dd}>
									{pbp.eventPlayer ? `${pbp.eventPlayer.firstName} ${pbp.eventPlayer.lastName}` : pbp?.playerOne?.displayName ?? 'Unknown'}
								</Typography>
							</Grid>
						</Grid>
					</>
				);
			},
			editComponent: (): JSX.Element => (
				<Select
					id='add-pbp-select'
					label='Play-by-play'
					value={selectedPbp}
					style={{ width: '100%' }}
					onChange={(e): void => setSelectedPbp(e.target.value as string)}>
					{getNonBypassedPbpData().map((pbp, idx) => (
						<MenuItem value={pbp.id} key={`NonPbP-${idx}`}>
							<strong style={{ marginRight: 10 }}>
								{pbp.minute}' {pbp.second}" {pbp.playType.toUpperCase()}
							</strong>
							<span style={{ color: 'red', marginRight: 10 }}>
								[
								{pbp.excitement_score && pbp.excitement_score !== undefined
									? `${pbp.excitement_score}%`
									: percentageFormatter(pbp?.scrubberInfo?.confidence ?? 0)}
								]
							</span>
							{pbp.eventPlayer ? `${pbp.eventPlayer.firstName} ${pbp.eventPlayer.lastName}` : pbp?.playOneName?.diplayName ?? 'Unknown'}
						</MenuItem>
					))}
				</Select>
			)
		}
	];

	const pbpComponents = {
		Container: (props: PaperProps): JSX.Element => <Paper {...props} elevation={0} data-testid='airings-table' />
	};

	const pbpEditable = { onRowAdd: addPbpBypass, onRowDelete: deletePbpBypass, isEditHidden: (): boolean => true };
	const pbpOptions = { maxBodyHeight: 425, paging: false, search: false, showTitle: false, sorting: false, addRowPosition: 'first' };

	const renderDialog = (airingId: string): JSX.Element => {
		return (
			<Dialog
				open={dialogOpen === airingId}
				onClose={cancelChallenge}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'>
				<DialogTitle id='alert-dialog-title'>
					{airingStatus === 'SCHEDULED_WITH_MAF' && (
						<>
							<AccessTimeIcon style={dialogIconStyle} />
							Set status to Scheduled
						</>
					)}
					{airingStatus === 'LIVE' && (
						<>
							<PlayCircleOutlineIcon style={dialogIconStyle} />
							Set status to In Progress
						</>
					)}
					{airingStatus === 'COMPLETED' && (
						<>
							<CheckCircleOutlineIcon style={dialogIconStyle} />
							Set status to Complete
						</>
					)}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id='alert-dialog-description'>
						Are you sure you want to change the status of the Airing on channel '<strong>{airingChannel}</strong>' to{' '}
						<strong>{fetchFriendlyStatus(airingStatus as Status)}</strong>?
					</DialogContentText>{' '}
					<DialogContentText id='alert-dialog-description-2'>
						Changing the status of an Airing <strong>may not</strong> have the effect you expect. The background system may yet update this status
						inspite of your change.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelChallenge} color='primary'>
						Cancel
					</Button>
					<Button onClick={updateAiringStatus} color='primary' autoFocus>
						I Understand - Proceed
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	const renderBypassModal = (): JSX.Element => {
		return (
			<Modal open={pbpConfModalOpen} className={classes.centralModal} onClose={(): void => cancelPbpConf()}>
				<Box className={classes.modalBox}>
					<Box className={classes.modalToolbar}>
						<Box className={classes.titleBox}>
							<CallMissedOutgoingIcon />
							<Typography variant='h5' style={{ paddingLeft: '0.75vw' }}>
								Bypass Play-by-Play Confidence/Excitement
							</Typography>
						</Box>
						<Button
							onClick={(): void => {
								cancelPbpConf();
							}}>
							<CloseIcon style={{ color: 'white', marginRight: '0.5vw' }} />
						</Button>
					</Box>
					<Box className={classes.modalContent}>
						<Box style={{ fontSize: '0.95rem' }}>
							<strong>Play-by-play</strong> events are automatically filtered by the <strong>confidence</strong> or <strong>excitement</strong> rating
							provided by MAF. Occasionally, poor confidence or low excitement is incorrectly assigned as a result of broadcast anomalies. Add events
							to the exclusion list below, and they will <strong>NOT</strong> be filtered by their confidence rating or low excitement.{' '}
							<strong>Note:</strong> This bypass has no effect on sport / business logic play type filtering.
						</Box>
						{airing.drm && drmHlsSource && mediaDataUrls.licenseUrl && (
							<Alert severity='info' style={{ marginTop: '0.5rem' }}>
								A VPN connection is required to watch a LIVE stream.
								<br />
								DRM protected content may cease to be viewable after the LIVE stream ends.
							</Alert>
						)}
						<MaterialTable
							data={getBypassedPbpData()}
							components={pbpComponents}
							columns={pbpColumns}
							editable={pbpEditable}
							options={pbpOptions}
							title='Bypassed PbP Confidence/Excitement'
						/>
					</Box>
					<Box className={classes.modalFooter}>
						<Button onClick={(): void => cancelPbpConf()} color='primary'>
							Cancel
						</Button>
						<Button
							onClick={savePbpBypass}
							color='primary'
							disabled={!bypassAltered}
							variant={bypassAltered ? 'contained' : 'text'}
							style={{ marginLeft: '1rem' }}>
							Save changes
						</Button>
					</Box>
				</Box>
			</Modal>
		);
	};

	return (
		<>
			<IconButton
				id='action-menu-button'
				aria-controls={open ? 'action-menu-menu' : undefined}
				aria-haspopup='true'
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
				size='small'>
				<MenuIcon />
			</IconButton>
			<Menu
				id='action-menu-menu'
				open={open}
				onClose={cancelChallenge}
				anchorEl={anchorEl}
				MenuListProps={{
					'aria-labelledby': 'action-menu-button'
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}>
				{status !== 'SCHEDULED_WITH_MAF' && (
					<MenuItem onClick={(): void => startChallenge(airingId, channel!.name!, 'SCHEDULED_WITH_MAF')}>
						<ListItemIcon style={{ marginRight: '-1rem', verticalAlign: 'middle' }}>
							<AccessTimeIcon />
						</ListItemIcon>
						<ListItemText>
							Set status to <strong>Scheduled</strong>
						</ListItemText>
					</MenuItem>
				)}
				{status !== 'LIVE' && (
					<MenuItem onClick={(): void => startChallenge(airingId, channel!.name!, 'LIVE')}>
						<ListItemIcon style={{ marginRight: '-1rem', verticalAlign: 'middle' }}>
							<PlayCircleOutlineIcon />
						</ListItemIcon>
						<ListItemText>
							Set status to <strong>In Progress</strong>
						</ListItemText>
					</MenuItem>
				)}
				{status !== 'COMPLETED' && (
					<MenuItem onClick={(): void => startChallenge(airingId, channel!.name!, 'COMPLETED')}>
						<ListItemIcon style={{ marginRight: '-1rem', verticalAlign: 'middle' }}>
							<CheckCircleOutlineIcon />
						</ListItemIcon>
						<ListItemText>
							Set status to <strong>Complete</strong>
						</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem disabled={status === 'SCHEDULED_WITH_MAF'} onClick={(): void => startPbpConf(airing)}>
					<ListItemIcon style={{ marginRight: '-1rem', verticalAlign: 'middle' }}>
						<CallMissedOutgoingIcon />
					</ListItemIcon>
					<ListItemText>
						<strong>Bypass</strong> PbP Confidence/Excitement
					</ListItemText>
				</MenuItem>
			</Menu>
			{renderDialog(airingId)}
			{renderBypassModal()}
		</>
	);
};

type AiringActionMenuProps = {
	airing: Airing;
	gameId: string;
	reloadAirings: Function;
	sport: string | null | undefined;
};

export default AiringActionMenu;
